import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Eye, Info, Loader2 } from "lucide-react";
import DocumentViewer from "../../../../../components/DocumentViewer.jsx";
import { API_GET_EMPLOYEE_FILE } from "../../../../../config/Endpoints.js";
import { FileField, RadioGroup, TextArea, TextField } from "../../../ui/index.ts";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { showError } from "../../../../utils/toast.ts";
import type { OffboardingFieldRendererProps } from "../types.ts";
import styles from "./question-renderers.module.css";

const TEXT_MAX_LENGTH = 2000;

function parseChoiceOptions(optionsJson: string | null | undefined): unknown[] {
    if (!optionsJson?.trim()) return [];

    try {
        const parsed: unknown = JSON.parse(optionsJson);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function OffboardingTextFieldRenderer({
    question,
    field,
    readOnly,
    index,
    required,
}: OffboardingFieldRendererProps) {
    if (readOnly) {
        return (
            <div className={styles.wrapper}>
                <label className={clsx("sub-heading-md", styles.label)} htmlFor={field.name}>
                    {question.question}
                </label>
                <p className={clsx("body-sm", styles.value)}>{field.state.value}</p>
            </div>
        );
    }

    const label =
        (index ?? false) !== false ? `${index}.  ${question.question}` : question.question;

    const fieldId = `offboarding-question-${question.questionId}`;

    if (question.questionId === 273) {
        return (
            <div className={styles.recoveryRow}>
                <TextField
                    id={fieldId}
                    label={label}
                    required={required}
                    inputMode="decimal"
                    value={typeof field.state.value === "string" ? field.state.value : ""}
                    onChange={(event) =>
                        field.handleChange(
                            event.target.value.replace(/[^0-9.]/g, ""),
                        )
                    }
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors?.[0]?.toString()}
                    placeholder="Enter recovery amount"
                />
                <div className={styles.policyNote} role="note">
                    <Info size={18} aria-hidden />
                    <span>
                        As per company policy, interns leaving before completion of the
                        internship period are required to pay an insurance premium of{" "}
                        {"\u20B9"}2,200.
                    </span>
                </div>
            </div>
        );
    }

    return (
        <TextArea
            id={fieldId}
            maxLength={TEXT_MAX_LENGTH}
            showCharacterCount
            label={label}
            required={required}
            value={typeof field.state.value === "string" ? field.state.value : ""}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            error={field.state.meta.errors?.[0]?.toString()}
            readOnly={readOnly}
            placeholder="Type your response here..."
        />
    );
}

export function OffboardingChoiceFieldRenderer({
    question,
    field,
    readOnly,
    index,
    required,
}: OffboardingFieldRendererProps) {
    const rawOptions = parseChoiceOptions(question.optionsJson);
    const visibleOptions =
        question.questionId === 272
            ? rawOptions.filter(
                  (option) => String(option).trim().toLowerCase() !== "not applicable",
              )
            : rawOptions;
    const formattedValues = visibleOptions.map((opt) => ({
        value: String(opt),
        label: String(opt),
    }));

    if (formattedValues.length === 0) {
        return (
            <OffboardingTextFieldRenderer
                question={question}
                field={field}
                readOnly={readOnly}
                index={index}
                required={required}
            />
        );
    }

    if (readOnly) {
        return (
            <div className={styles.wrapper}>
                <label className={clsx("sub-heading-md", styles.label)} htmlFor={field.name}>
                    {question.question}
                </label>
                <p className={clsx("body-sm", styles.choiceValue)}>{field.state.value}</p>
            </div>
        );
    }

    const label =
        (index ?? false) !== false ? `${index}.  ${question.question}` : question.question;

    const radioGroupId = `offboarding-question-${question.questionId}`;

    return (
        <RadioGroup
            name={radioGroupId}
            id={radioGroupId}
            label={label}
            required={required}
            value={typeof field.state.value === "string" ? field.state.value : ""}
            onChange={(value) => field.handleChange(value)}
            onBlur={field.handleBlur}
            error={field.state.meta.errors?.[0]?.toString()}
            options={formattedValues}
            disabled={readOnly}
            layout="grid"
        />
    );
}

export function OffboardingFileFieldRenderer({
    question,
    field,
    readOnly,
    index,
    required,
}: OffboardingFieldRendererProps) {
    const [viewerUrl, setViewerUrl] = useState<string | null>(null);
    const [viewerType, setViewerType] = useState("application/octet-stream");
    const [viewerName, setViewerName] = useState("Insurance Document");
    const [viewLoading, setViewLoading] = useState(false);
    const label =
        (index ?? false) !== false ? `${index}.  ${question.question}` : question.question;

    const closeViewer = useCallback(() => {
        setViewerUrl((current) => {
            if (current) URL.revokeObjectURL(current);
            return null;
        });
    }, []);

    useEffect(() => closeViewer, [closeViewer]);

    useEffect(() => {
        if (!viewerUrl) return;
        const element = document.getElementById("staticBackdropDocumentViewer");
        if (element) {
            const modal = new window.bootstrap.Modal(element, { backdrop: false });
            modal.show();
        }
    }, [viewerUrl]);

    const viewInsuranceDocument = useCallback(async () => {
        const path = question.insurancePath;
        if (!path) return;
        setViewLoading(true);
        try {
            const url = `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(path)}`;
            const { data: blob } = await getApi<Blob>(url, accessToken(), "blob");
            const objectUrl = URL.createObjectURL(blob);
            setViewerUrl((current) => {
                if (current) URL.revokeObjectURL(current);
                return objectUrl;
            });
            setViewerType(blob.type || "application/octet-stream");
            setViewerName(
                question.answer?.trim() ||
                    path.split("/").filter(Boolean).pop() ||
                    "Insurance Document",
            );
        } catch (error) {
            showError(
                error instanceof Error
                    ? error.message
                    : "Failed to load insurance document.",
            );
        } finally {
            setViewLoading(false);
        }
    }, [question.answer, question.insurancePath]);

    const viewButton = question.insurancePath ? (
        <button
            type="button"
            className={styles.viewButton}
            disabled={viewLoading}
            onClick={() => void viewInsuranceDocument()}
        >
            {viewLoading ? (
                <Loader2 size={14} className={styles.spin} aria-hidden />
            ) : (
                <Eye size={14} aria-hidden />
            )}
            {viewLoading ? "Loading…" : "View"}
        </button>
    ) : null;

    const documentViewer = viewerUrl ? (
        <DocumentViewer
            url={viewerUrl}
            contentType={viewerType}
            fileName={viewerName}
            onClose={closeViewer}
        />
    ) : null;

    if (readOnly) {
        const value = field.state.value;
        const fileName = value instanceof File ? value.name : String(value || "—");
        return (
            <>
                <div className={styles.wrapper}>
                    <p className={clsx("sub-heading-md", styles.label)}>{question.question}</p>
                    <div className={styles.fileAnswerRow}>
                        <p className={clsx("body-sm", styles.value)}>{fileName}</p>
                        {viewButton}
                    </div>
                </div>
                {documentViewer}
            </>
        );
    }

    const selectedFile = field.state.value instanceof File ? field.state.value : null;

    return (
        <>
            <FileField
                id={`offboarding-question-${question.questionId}`}
                label={label}
                required={required}
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                stagedFile={selectedFile}
                onChange={(file) => field.handleChange(file ?? "")}
                onBlur={field.handleBlur}
                error={field.state.meta.errors?.[0]?.toString()}
                hint="PDF, JPG, JPEG, or PNG"
            />
            {viewButton}
            {documentViewer}
        </>
    );
}

export function OffboardingQuestionRenderer({
    question,
    field,
    readOnly,
    index,
    required,
}: OffboardingFieldRendererProps) {
    switch (question.questionType) {
        case "TEXT":
            return (
                <OffboardingTextFieldRenderer
                    question={question}
                    field={field}
                    readOnly={readOnly}
                    index={index}
                    required={required}
                />
            );
        case "RADIO":
        case "RATING":
            return (
                <OffboardingChoiceFieldRenderer
                    question={question}
                    field={field}
                    readOnly={readOnly}
                    index={index}
                    required={required}
                />
            );
        case "FILE":
            return (
                <OffboardingFileFieldRenderer
                    question={question}
                    field={field}
                    readOnly={readOnly}
                    index={index}
                    required={required}
                />
            );
        default:
            return (
                <div className="body-sm">
                    Unsupported question type. Please contact the administrator.
                </div>
            );
    }
}
